import { Spacing } from '@persona-id/design-tokens';
import { VFC } from 'react';
import styled from 'styled-components';

import ParamLabel from 'dashboard/components/WorkflowControlPanel/ParamLabel';
import ActionOutputHttpRequestV1 from 'dashboard/components/WorkflowControlPanel/StepConfigAction/ActionOutput/ActionOutputHttpRequestV1';
import ActionOutputRunHttpPlatformIntegrationV1 from 'dashboard/components/WorkflowControlPanel/StepConfigAction/ActionOutput/ActionOutputRunHttpPlatformIntegrationV1';
import ActionOutputSendEmailV1 from 'dashboard/components/WorkflowControlPanel/StepConfigAction/ActionOutput/ActionOutputSendEmailV1';
import ActionOutputSendSmsV1 from 'dashboard/components/WorkflowControlPanel/StepConfigAction/ActionOutput/ActionOutputSendSmsV1';
import ConsoleOutput from 'dashboard/components/WorkflowControlPanel/StepConfigAction/ConsoleOutput';
import CodeHighlight from 'dashboard/components-lib/CodeHighlight';
import { WorkflowRunStepAction } from 'dashboard/models/workflow-step';
import {
  WorkflowActionCalculateV1,
  WorkflowActionSendEmailV1,
  WorkflowActionSendSmsV1,
  WorkflowStepAction,
  WorkflowStepActionKey,
} from 'dashboard/models/workflow-version';
import Spacer from 'design-system/components/Spacer';
import { headingMixins } from 'design-system/mixins/text';
import { ApiModel } from 'lib/interfaces/api';

const StyledCodeHighlight = styled(CodeHighlight)`
  border: 1px solid ${({ theme }) => theme.color.border.input};
  border-radius: ${({ theme }) => theme.element.borderRadius};
`;

const SubTitle = styled.div`
  ${headingMixins.sm}
  padding-bottom: ${Spacing['4']};
`;

interface ActionOutputProps {
  workflowRunStep: WorkflowRunStepAction;
  currentStep: WorkflowStepAction;
}

const ActionOutput: VFC<ActionOutputProps> = ({ workflowRunStep, currentStep }) => {
  const output =
    workflowRunStep?.attributes?.['known-variables-map']?.[
      workflowRunStep.attributes['config-step-token']
    ]?.output;

  const generatedVariable = workflowRunStep?.attributes['generated-variable'];
  if (
    generatedVariable &&
    generatedVariable.type === ApiModel.WorkflowRunVariablePlatformIntegration
  ) {
    return <ActionOutputRunHttpPlatformIntegrationV1 output={generatedVariable.variable} />;
  }

  if (output == null || output === '' || Object.keys(output).length === 0) {
    return null;
  }

  const outputContent = (() => {
    switch (currentStep.action.key) {
      case WorkflowStepActionKey.SendEmail: {
        // [Backwards compatibilbity] Email step used to have resolved as the key of the output
        const result = output.resolved ? output.resolved : (output.result ?? null);
        return <ActionOutputSendEmailV1 params={result as WorkflowActionSendEmailV1['params']} />;
      }
      case WorkflowStepActionKey.SendSms: {
        // [Backwards compatibilbity] SMS step used to have resolved as the key of the output
        const result = output.resolved ? output.resolved : (output.result ?? null);
        return <ActionOutputSendSmsV1 params={result as WorkflowActionSendSmsV1['params']} />;
      }
      case WorkflowStepActionKey.Calculate: {
        const params = currentStep.action.params as WorkflowActionCalculateV1['params'];
        const displayOutput = Object.entries(output).reduce(
          (hash: Record<string, any>, [outputId, value]) => {
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            const name = params.variables.find(({ id }) => id === outputId).name;
            hash[name] = value;
            return hash;
          },
          {},
        );
        return (
          <StyledCodeHighlight language='json' light>
            {JSON.stringify(displayOutput, null, 2)}
          </StyledCodeHighlight>
        );
      }
      case WorkflowStepActionKey.EvaluateCode: {
        const { logs, result } = output;
        return (
          <>
            <ParamLabel labelText='Returned Value' htmlFor='action-output__evaluate-code-result' />
            <output id='action-output__evaluate-code-result'>
              <StyledCodeHighlight language='json' light>
                {JSON.stringify(result, null, 2)}
              </StyledCodeHighlight>
            </output>
            {(logs?.length ?? 0) > 0 && (
              <>
                <Spacer height={2} />
                <ParamLabel
                  labelText='Console Output'
                  htmlFor='action-output__evaluate-code-logs'
                />
                <output id='action-output__evaluate-code-logs'>
                  <ConsoleOutput logs={logs} />
                </output>
              </>
            )}
          </>
        );
      }
      case WorkflowStepActionKey.HttpRequest:
        return <ActionOutputHttpRequestV1 output={output} />;
      default:
        return null; // No selectable output
    }
  })();

  return outputContent != null ? (
    <>
      <SubTitle>Output</SubTitle>
      {outputContent}
    </>
  ) : null;
};

export default ActionOutput;
